import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ReceiptText,
  BookOpen,
  BarChart3,
  MoreHorizontal,
  ShoppingCart,
  ShoppingBasket,
} from 'lucide-react';
import { isPathMatch } from '../data/navigation';

function TabItem({ to, icon: Icon, label, active, onClick }) {
  return (
    <NavLink
      to={to}
      className={`app-bottom-nav__tab${active ? ' app-bottom-nav__tab--active' : ''}`}
      onClick={onClick}
    >
      <span className="app-bottom-nav__icon">
        <Icon size={20} strokeWidth={active ? 2.4 : 2} />
      </span>
      <span className="app-bottom-nav__label">{label}</span>
    </NavLink>
  );
}

/**
 * AppBottomNav — mobile-only bottom tab bar.
 *
 * 5 slots: Dashboard · Transaksi (popup: Jual/Beli) · Katalog · Laporan ·
 * Lainnya (opens the full hamburger-drawer menu).
 */
export default function AppBottomNav({ onOpenMore }) {
  const { pathname } = useLocation();
  const [txOpen, setTxOpen] = useState(false);
  const txRef = useRef(null);

  const closeTx = () => setTxOpen(false);

  useEffect(() => {
    const handler = (e) => {
      if (txRef.current && !txRef.current.contains(e.target)) setTxOpen(false);
    };
    if (txOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [txOpen]);

  const isTxActive =
    isPathMatch(pathname, '/sales') ||
    isPathMatch(pathname, '/purchases') ||
    isPathMatch(pathname, '/returns');
  const isKatalogActive =
    isPathMatch(pathname, '/catalogue') ||
    isPathMatch(pathname, '/promotions') ||
    isPathMatch(pathname, '/highlights');

  return (
    <nav className="app-bottom-nav" aria-label="Navigasi utama">
      <TabItem
        to="/dashboard"
        icon={LayoutDashboard}
        label="Dashboard"
        active={pathname === '/dashboard'}
        onClick={closeTx}
      />

      <div ref={txRef} className="app-bottom-nav__tabwrap">
        <button
          type="button"
          className={`app-bottom-nav__tab${
            isTxActive ? ' app-bottom-nav__tab--active app-bottom-nav__tab--tx' : ''
          }`}
          aria-haspopup="menu"
          aria-expanded={txOpen}
          onClick={() => setTxOpen((o) => !o)}
        >
          <span className="app-bottom-nav__icon">
            <ReceiptText size={20} strokeWidth={isTxActive ? 2.4 : 2} />
          </span>
          <span className="app-bottom-nav__label">Transaksi</span>
        </button>

        {txOpen && (
          <div className="app-bottom-nav__pop">
            <NavLink to="/sales" className="app-bottom-nav__pop-item" onClick={closeTx}>
              <span className="app-bottom-nav__pop-icon"><ShoppingCart size={22} /></span>
              <span>Jual</span>
            </NavLink>
            <NavLink to="/purchases" className="app-bottom-nav__pop-item" onClick={closeTx}>
              <span className="app-bottom-nav__pop-icon"><ShoppingBasket size={22} /></span>
              <span>Beli</span>
            </NavLink>
          </div>
        )}
      </div>

      <TabItem
        to="/catalogue"
        icon={BookOpen}
        label="Katalog"
        active={isKatalogActive}
        onClick={closeTx}
      />
      <TabItem
        to="/reports"
        icon={BarChart3}
        label="Laporan"
        active={isPathMatch(pathname, '/reports')}
        onClick={closeTx}
      />

      <button
        type="button"
        className="app-bottom-nav__tab app-bottom-nav__tab--more"
        onClick={onOpenMore}
        aria-label="Buka menu lengkap"
      >
        <span className="app-bottom-nav__icon">
          <MoreHorizontal size={20} strokeWidth={2} />
        </span>
        <span className="app-bottom-nav__label">Lainnya</span>
      </button>
    </nav>
  );
}
